import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Button, Divider } from 'components/ui';
import Radio from 'components/Inputs/Radio/Radio';
import PaymentDetails from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentDetails';
import anaConsts from 'analytics/constants';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';

/**
 * Renders a saved alternate payment method option (PayPal, Klarna, Afterpay, etc.)
 * as a radio button with payment details and a "Use This Payment Method" button.
 * Supports disabled state when the payment method is not available for the current order.
 *
 * @param {Object} props - Component props
 * @param {string} props.paymentName - Display name of the payment method
 * @param {string} props.defaultPayment - Identifier for the default payment method
 * @param {boolean} props.selected - Whether this payment option is currently selected
 * @param {Function} props.onClick - Handler called when radio button is clicked
 * @param {Function} props.onUseThisPaymentMethod - Handler called when "Use This Payment Method" button is clicked
 * @param {string} props.installmentValue - Installment payment details (for buy-now-pay-later options)
 * @param {string} props.username - Username/email associated with the payment method
 * @param {boolean} props.isPayPal - Whether this is a PayPal payment method
 * @param {Object} props.locales - Localized strings for UI text
 * @param {boolean} [props.isAvailable=true] - Whether this payment method is available for the current order
 * @returns {JSX.Element} Saved alternate payment radio button component
 */
const SavedAlternatePaymentRadio = ({
    paymentName,
    defaultPayment,
    selected,
    onClick,
    onUseThisPaymentMethod,
    installmentValue,
    username,
    isPayPal,
    locales,
    isAvailable = true
}) => {
    const hasTrackedButtonStateRef = useRef(false);

    useEffect(() => {
        if (selected && !hasTrackedButtonStateRef.current) {
            hasTrackedButtonStateRef.current = true;
            FrictionlessCheckoutBindings.setPaymentSectionButtonStateAnalytics(anaConsts.ACTION_INFO.PAYMENT_USE_THIS_PAYMENT_METHOD);
        }
    }, [selected]);

    return (
        <>
            <Box paddingTop={4}>
                <Radio
                    paddingY={0}
                    dotOffset={0}
                    alignItems='center'
                    name='savedAlternatePayment'
                    checked={selected}
                    onClick={onClick}
                    disabled={!isAvailable}
                    data-at={Sephora.debug.dataAt('saved_alternate_payment_radio')}
                >
                    <PaymentDetails
                        paymentName={!isPayPal && paymentName}
                        defaultPayment={defaultPayment}
                        selected={selected}
                        checkoutEnabled={true}
                        installmentValue={installmentValue}
                        username={username}
                        isSaveState={true}
                        isPayPal={isPayPal}
                        paypalEmail={isPayPal && username}
                    />
                </Radio>

                {selected && (
                    <>
                        <Box
                            marginTop={4}
                            marginBottom={4}
                        >
                            <Button
                                variant='primary'
                                width={['100%', '100%', '380px']}
                                onClick={onUseThisPaymentMethod}
                                disabled={!isAvailable}
                                data-at={Sephora.debug.dataAt('use_this_payment_method_btn')}
                            >
                                {locales.useThisPaymentMethod}
                            </Button>
                        </Box>
                    </>
                )}
            </Box>

            <Divider marginTop={selected ? 0 : 4} />
        </>
    );
};

SavedAlternatePaymentRadio.propTypes = {
    paymentName: PropTypes.string.isRequired,
    defaultPayment: PropTypes.string,
    selected: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired,
    onUseThisPaymentMethod: PropTypes.func.isRequired,
    installmentValue: PropTypes.string,
    username: PropTypes.string,
    locales: PropTypes.object.isRequired,
    isAvailable: PropTypes.bool
};

export default wrapFunctionalComponent(SavedAlternatePaymentRadio, 'SavedAlternatePaymentRadio');
